import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import AHDateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import DocumentNameSchema from "documents/names/_schema/document-name-schema";
import DocumentTopicSchema from "documents/topics/_schema/document-topic-schema";
import { DocumentClassificationStatus } from "documents/classification/document-classification-status";
import { DocumentClassificationSource } from "documents/classification/document-classification-source";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";
import { DocumentFileType } from "documents/document-file-type";
import { DocumentContentAccess } from "documents/classification/document-content-access";
import { DocumentFileScanStatus } from "documents/downloads/document-file-scan-status";
import { z } from "zod";

export type DocumentJSON = z.infer<typeof DocumentSchema.DocumentJSONZ>;
export type DocumentParams = z.infer<typeof DocumentSchema.DocumentZ>;

/**
 * The API names a label by its enum name ("Financial"), not its number. A name this build does not
 * know is dropped rather than failing the whole document: the list still has to render.
 */
function subjectMatterFromName(name: string): DocumentSubjectMatter | undefined {
  const value = DocumentSubjectMatter[name as keyof typeof DocumentSubjectMatter];
  return typeof value === "number" ? value : undefined;
}

export default class DocumentSchema {
  public static readonly DocumentJSONZ = z.object({
    id: z.uuid(),
    creatorId: z.uuid().nullish(),
    fileSize: z.number().nullish(),
    fileType: z.string().refine((val) => Object.values(DocumentFileType).includes(val as DocumentFileType)),
    created: z.iso.datetime({ offset: true }).nullish(),
    readAccess: z.array(z.uuid()).nullish(),
    writeAccess: z.array(z.uuid()).nullish(),
    hasSystemPermissions: z.boolean().nullish(),
    name: z.string(),
    topics: z.array(DocumentTopicSchema.DocumentTopicJSONZ).nullish(),
    templateIds: z.array(z.uuid()).nullish(),
    isArchived: z.boolean().nullish(),
    inUse: z.boolean().nullish(),
    // Nullish rather than defaulted. An absent status means the payload did not say, which is
    // not the same as Pending - and Pending is 0, so a default would also be indistinguishable
    // from the value itself under any falsy check.
    classificationStatus: z.enum(DocumentClassificationStatus).nullish(),
    // Present only when the reader may see the labels. Absent is "not yours to see" or "not
    // classified yet", and is never the same as an empty list - see `Document.subjectMatters`.
    subjectMatters: z.array(z.string()).nullish(),
    classificationContentHash: z.string().nullish(),
    classificationSource: z.enum(DocumentClassificationSource).nullish(),
    classificationOverriddenByUserId: z.uuid().nullish(),
    classificationOverriddenByName: z.string().nullish(),
    classificationOverriddenAt: z.iso.datetime({ offset: true, local: true }).nullish(),
    canOverrideClassification: z.boolean().nullish(),
    // What the API decided for this reader (#6992). Absent where the payload did not say - a
    // realtime event, an older API - and that is no evidence of a refusal.
    contentAccess: z.enum(DocumentContentAccess).nullish(),
    // The labels the reader lacks, by name. Sent only with SubjectMatterRestricted.
    restrictedSubjectMatters: z.array(z.string()).nullish(),
    scanStatus: z.enum(DocumentFileScanStatus).nullish(),
  });
  public static readonly DocumentZ = z.object({
    id: z.instanceof(Guid),
    creatorId: z.instanceof(Guid).optional(),
    fileSize: z.number().optional(),
    fileType: z.enum(DocumentFileType),
    created: AHDateSchema.type.optional(),
    readAccess: z.array(z.instanceof(Guid)).optional(),
    writeAccess: z.array(z.instanceof(Guid)).optional(),
    hasSystemPermissions: z.boolean().optional(),
    name: DocumentNameSchema.DocumentNameZ,
    topics: z.array(DocumentTopicSchema.DocumentTopicZ).optional(),
    templateIds: z.array(z.instanceof(Guid)).optional(),
    replacesId: z.instanceof(Guid).optional(),
    isArchived: z.boolean().optional(),
    inUse: z.boolean().optional(),
    classificationStatus: z.enum(DocumentClassificationStatus).optional(),
    subjectMatters: z.array(z.enum(DocumentSubjectMatter)).optional(),
    classificationContentHash: z.string().optional(),
    classificationSource: z.enum(DocumentClassificationSource).optional(),
    classificationOverriddenByUserId: z.instanceof(Guid).optional(),
    classificationOverriddenByName: z.string().optional(),
    classificationOverriddenAt: AHDateSchema.type.optional(),
    canOverrideClassification: z.boolean().optional(),
    contentAccess: z.enum(DocumentContentAccess).optional(),
    restrictedSubjectMatters: z.array(z.enum(DocumentSubjectMatter)).optional(),
    scanStatus: z.enum(DocumentFileScanStatus).optional(),
  });

  public static validate(values: unknown): DocumentParams {
    return parseOrThrow(DocumentSchema.DocumentZ, values, {
      logError: true,
      schemaName: "DocumentSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): DocumentJSON {
    return parseOrThrow(DocumentSchema.DocumentJSONZ, values, {
      logError: true,
      schemaName: "DocumentSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is DocumentParams {
    try {
      DocumentSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is DocumentJSON {
    try {
      DocumentSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): DocumentParams {
    const parsed = parseOrThrow(DocumentSchema.DocumentJSONZ, json, {
      logError: true,
      schemaName: "DocumentSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return DocumentSchema.codec.decode(parsed);
  }

  public static serialize(document: DocumentParams): DocumentJSON {
    const parsed = DocumentSchema.validate(document);
    return DocumentSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(DocumentSchema.DocumentJSONZ, DocumentSchema.DocumentZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      creatorId: Guid.fromJSON(data.creatorId) ?? undefined,
      fileSize: data.fileSize ?? undefined,
      fileType: DocumentFileType[enumKeyOf(DocumentFileType, data.fileType) as keyof typeof DocumentFileType],
      created: AHDate.fromJSON(data.created) ?? undefined,
      readAccess: Guid.fromJSONArray(data.readAccess) ?? undefined,
      writeAccess: Guid.fromJSONArray(data.writeAccess) ?? undefined,
      hasSystemPermissions: Boolean(data.hasSystemPermissions ?? false),
      name: DocumentNameSchema.deserialize(data.name),
      topics: data.topics?.map(DocumentTopicSchema.deserialize),
      templateIds: Guid.fromJSONArray(data.templateIds) ?? undefined,
      isArchived: data.isArchived ?? undefined,
      inUse: data.inUse ?? false,
      // `??` and not `||`: DocumentClassificationStatus.Pending is 0, and `||` would turn a
      // document that is still processing into one whose status was never sent.
      classificationStatus: data.classificationStatus ?? undefined,
      subjectMatters: data.subjectMatters
        ?.map(subjectMatterFromName)
        .filter((value): value is DocumentSubjectMatter => value !== undefined),
      classificationContentHash: data.classificationContentHash ?? undefined,
      // `??` again: Automatic is 0.
      classificationSource: data.classificationSource ?? undefined,
      classificationOverriddenByUserId: Guid.fromJSON(data.classificationOverriddenByUserId) ?? undefined,
      classificationOverriddenByName: data.classificationOverriddenByName ?? undefined,
      classificationOverriddenAt: AHDate.fromJSON(data.classificationOverriddenAt) ?? undefined,
      canOverrideClassification: data.canOverrideClassification ?? undefined,
      // `??` again: Allowed is 0.
      contentAccess: data.contentAccess ?? undefined,
      restrictedSubjectMatters: data.restrictedSubjectMatters
        ?.map(subjectMatterFromName)
        .filter((value): value is DocumentSubjectMatter => value !== undefined),
      scanStatus: data.scanStatus ?? undefined,
    }),
    encode: (document) => ({
      id: document.id.toJSON(),
      creatorId: document.creatorId ? document.creatorId.toJSON() : undefined,
      fileSize: document.fileSize,
      fileType: document.fileType.valueOf(),
      created: document.created?.toJSON(),
      readAccess: document.readAccess?.map((guid) => guid.toJSON()),
      writeAccess: document.writeAccess?.map((guid) => guid.toJSON()),
      hasSystemPermissions: document.hasSystemPermissions ?? false,
      name: document.name.toJSON(),
      topics: document.topics?.map(DocumentTopicSchema.serialize),
      templateIds: document.templateIds?.map((guid) => guid.toJSON()),
      isArchived: document.isArchived,
      inUse: document.inUse ?? false,
      classificationStatus: document.classificationStatus,
      subjectMatters: document.subjectMatters?.map((value) => DocumentSubjectMatter[value]),
      classificationContentHash: document.classificationContentHash,
      classificationSource: document.classificationSource,
      classificationOverriddenByUserId: document.classificationOverriddenByUserId?.toJSON(),
      classificationOverriddenByName: document.classificationOverriddenByName,
      classificationOverriddenAt: document.classificationOverriddenAt?.toJSON(),
      canOverrideClassification: document.canOverrideClassification,
      contentAccess: document.contentAccess,
      restrictedSubjectMatters: document.restrictedSubjectMatters?.map((value) => DocumentSubjectMatter[value]),
      scanStatus: document.scanStatus,
    }),
  });
}
